import { apiServerUrl, ExternalApi } from "@/apis/apiService";
import {
    useSessionStore,
    useCheckoutStore,
    useTruelayerStore,
} from "@/plugins/store";
import {
    TruelayerInstitutionsRequestData,
    TruelayerInstitutionsResponse,
    TruelayerCreatePaymentResponse,
    TruelayerSelectedBank,
    TruelayerCreatePaymentPayload,
} from "@/helpers/interfaces/providers";
import { getPaymentMethodAmount } from "@/helpers/paymentMethodHelpers";
import { keysToCamelCase, keysToSnakeCase } from "@/helpers/stringProcessors";
import { createPaymentPayload } from "@/helpers/payment";
import i18n from "@/plugins/vi18n";
import { getRequestConfig } from "@esenda/core-ui/utils";
import { getCommonHeaderParams } from "@/helpers/api";
import { HTTP_METHOD } from "@esenda/core-ui/constants";
import { useOrganisationStore } from "@/plugins/store";

export const getInstitutions = async () => {
    /**
     * Internal API call to retrieve Truelayer's list of bank institutions.
     * It returns null on errors and triggers the alert component.
     * @return {[{TruelayerInstitutionsResponse | null}]}
     */
    const sessionStore = useSessionStore();
    const orgStore = useOrganisationStore();

    const amount = getPaymentMethodAmount(false); /** not formatted amount */
    const requestPayload: TruelayerInstitutionsRequestData = {
        shortcode: orgStore.shortcode,
        country: sessionStore.countryCode,
        amount: String(amount),
    };

    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: "refresh", // user needs to refresh the page
        sentryTransactionName: "getTruelayerInstitutionsAPI",
    };

    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/providers/truelayer/banks`,
            HTTP_METHOD.POST,
            getCommonHeaderParams(),
            requestPayload,
        ),
        errorHandlerConfig,
    );

    if (data !== null) {
        const institutionList: TruelayerInstitutionsResponse[] =
            keysToCamelCase(data);
        return institutionList;
    } else {
        return null;
    }
};

export const createPayment = async (selectedBank: TruelayerSelectedBank) => {
    /**
     * Calls Truelayer's create payment endpoint to start the payment.
     * @param {{TruelayerSelectedBank}}
     * @return {[{TruelayerCreatePaymentResponse | null}]}
     */
    const tlStore = useTruelayerStore();
    const checkoutStore = useCheckoutStore();

    const providerInformation = {
        ...selectedBank,
        ...checkoutStore.payerInformation,
    };

    const paymentPayload = keysToSnakeCase(
        createPaymentPayload(
            providerInformation as TruelayerCreatePaymentPayload,
        ),
    );

    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: "retry", // user needs to retry the action
        sentryTransactionName: "createPayment",
        redirectTo: "payment-status", // redirect to the payment status page if possible
    };

    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/providers/truelayer/payment`,
            HTTP_METHOD.POST,
            getCommonHeaderParams(),
            paymentPayload,
        ),
        errorHandlerConfig,
    );

    if (data !== null) {
        const paymentResponse: TruelayerCreatePaymentResponse =
            keysToCamelCase(data);
        tlStore.setTruelayerPaymentInformation(paymentResponse);
        /** Redirects the user to Truelayer's HPP url with the language code attached */
        const updatedRedirectUrl = updateUrlWithLanguage(
            paymentResponse.redirectUrl,
        );
        window.location.href = updatedRedirectUrl;
        return paymentResponse;
    } else {
        return null;
    }
};

const updateUrlWithLanguage = (url: string): string => {
    /**
     * Updates the TL URL by ensuring the language parameter lng is first in the fragment.
     * @param {string} url
     * @returns {string} - the updated URL with `lng` placed at the beginning of the fragment
     */
    if (!url || typeof url !== "string") {
        return url;
    }

    const languageParam = getTruelayerUILanguage();
    const [baseUrl, fragment] = url.split("#");
    if (!fragment) {
        return url;
    }

    const fragmentParams = new URLSearchParams(fragment);

    return `${baseUrl}#lng=${languageParam}&${fragmentParams.toString()}`;
};

const getTruelayerUILanguage = () => {
    /***
     * Maps the user locale to the list of supported languages.
     * If the language exists in the TL list & is supported by the internationalisation
     * plugin, it will return a language query param to attach to the redirect link.
     * Otherwise, it returns will default to English.
     *
     * TL lng docs: https://docs.truelayer.com/docs/customise-the-hpp#override-the-users-system-language
     * @returns {string} - lng query param or empty string
     */
    const sessionStore = useSessionStore();
    const locale = sessionStore.language ?? "en-GB";
    const availableLocales = i18n.global.availableLocales;
    const isLocaleSupported = availableLocales.includes(locale);

    const languageCode = locale?.slice(0, 2) || "en";
    // TL supports the following languages
    const tlSupportedLanguages = [
        "en",
        "es",
        "fr",
        "de",
        "nl",
        "pt",
        "pl",
        "fi",
        "lt",
        "it",
    ];

    return tlSupportedLanguages.includes(languageCode) && isLocaleSupported
        ? languageCode
        : "en";
};
